import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { PoliticallyExposedPersonClass } from 'dashboard/models/report/politically-exposed-person';
import { WatchlistType } from 'dashboard/models/report/watchlist';
import { MatchGroup } from 'dashboard/models/verification-check';
import { ApiModel, ApiResource } from 'lib/interfaces/api';

export enum MatchFuzzinessLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum MatchPreset {
  EXACT = 'exact',
  STRICT = 'strict',
  TOLERANT = 'tolerant',
  CUSTOM = 'custom',
}

export enum BirthdateMatchType {
  DayExact = 'day_exact',
  YearExact = 'year_exact',
  PlusOrMinusOneYear = 'plus_or_minus_one_year',
}

export interface ReportTemplateVersionLegacy<E = unknown> {
  id: number;
  name?: string;
  data?: E;
  config?: E;
}

export interface ReportTemplateVersionAttributesPoliticallyExposedPerson {
  'recurring-enabled'?: boolean;
  'always-recur'?: boolean;
  'interval-seconds'?: number;
  'enabled-classes'?: PoliticallyExposedPersonClass[];
  'country-codes'?: string[];
  'match-preset'?: MatchPreset;
  'aka-exact-enabled'?: boolean;
  'partial-names-enabled'?: boolean;
  'equivalent-names-enabled'?: boolean;
  'fuzziness-level'?: MatchFuzzinessLevel;
}

export interface ReportTemplateVersionAttributesAdverseMedia {
  'search-criteria'?: string[];
  'recurring-enabled'?: boolean;
  'always-recur'?: boolean;
  'interval-seconds'?: number;
  'article-age-maximum-days'?: number;
  'article-language-allowlist'?: string[];
  'person-age-when-article-published-minimum'?: number;
  'only-articles-with-published-dates'?: boolean;
  'only-articles-with-birthdates'?: boolean;
  'birthdate-match-type'?: 'day-exact' | 'year-exact' | undefined;
  'match-preset'?: MatchPreset;
  'aka-exact-enabled'?: boolean;
  'partial-names-enabled'?: boolean;
  'equivalent-names-enabled'?: boolean;
  'fuzziness-level'?: MatchFuzzinessLevel;
}

export interface ReportTemplateVersionAttributesBusinessAdverseMedia {
  'search-criteria'?: string[];
  'recurring-enabled'?: boolean;
  'always-recur'?: boolean;
  'interval-seconds'?: number;
  'article-age-maximum-days'?: number;
  'only-articles-with-published-dates'?: boolean;
  'match-preset'?: MatchPreset;
  'aka-exact-enabled'?: boolean;
  'partial-names-enabled'?: boolean;
  'equivalent-names-enabled'?: boolean;
  'fuzziness-level'?: MatchFuzzinessLevel;
}

export interface ReportTemplateVersionAttributesBusinessAssociatedPersons {
  'sayari-entity-search-timeout-seconds'?: number;
  'entity-resolution-business-match-groups'?: any[];
  'entity-resolution-person-match-groups'?: any[];
}

export interface ReportTemplateVersionAttributesBusinessClassification {
  'high-risk-industries'?: string[];
}
export interface ReportTemplateVersionAttributesBusinessWatchlist {
  'source-category-types'?: WatchlistType[];
  'sanctions-enabled'?: boolean;
  'warnings-enabled'?: boolean;
  'fitness-probity-enabled'?: boolean;
  'recurring-enabled'?: boolean;
  'always-recur'?: boolean;
  'interval-seconds'?: number;
  'match-preset'?: MatchPreset;
  'aka-exact-enabled'?: boolean;
  'partial-names-enabled'?: boolean;
  'equivalent-names-enabled'?: boolean;
  'fuzziness-level'?: MatchFuzzinessLevel;
}

export interface ReportTemplateVersionAttributesCryptoAddressWatchlist {
  'recurring-enabled'?: boolean;
  'interval-seconds'?: number;
  'always-recur'?: boolean;
}

export enum CustomListFileType {
  FincenPerson = 'fincen_person',
  FincenBusiness = 'fincen_business',
  CustomScreening = 'custom_screening',
}

export interface CustomListConfigMappableField {
  name: string;
  type: string;
}

export interface CustomListConfigHeaderMapping {
  'header-name': string;
  'mapped-field': string;
}

export interface ReportTemplateVersionAttributesCustomList {
  name: string;
  'run-policy': 'on_list_upload' | 'always' | 'manual';
  'custom-list-file-name': string;
  'custom-list-file-uploaded-at': string;
  'custom-list-file-type': CustomListFileType;
  'change-description': string | undefined;
  'created-at': string;
  'custom-list': string[][];
  'total-count': number;
  'in-progress-count': number;
  'matched-count': number;
  'completed-count': number;
  'run-status': 'not_started' | 'running' | 'complete' | 'failed';
  description?: string;
  'mappable-fields': Array<CustomListConfigMappableField>;
  'header-mappings': Array<CustomListConfigHeaderMapping>;
  'match-groups': Array<MatchGroup>;
}

export type ReportTemplateVersionCustomListRunPayload = ApiResource<
  {
    description?: string;
  },
  void,
  ApiModel.ReportTemplateVersionCustomList
>;

export type ReportTemplateVersionCustomListValidateHeaderMappingsPayload = ApiResource<
  {
    'header-mappings': Array<CustomListConfigHeaderMapping>;
    'custom-list-file': FileWithPreview;
  },
  void,
  ApiModel.ReportTemplateVersionCustomList
>;

export interface ReportTemplateVersionAttributesScreening {
  // TODO(maggie): this is definitely not correct
  config?: object;
}

export type WatchlistPresetType = 'recommended' | 'more_matches' | 'custom' | MatchPreset;

export interface ReportTemplateVersionAttributesWatchlist {
  'source-category-types'?: WatchlistType[];
  'data-sources'?: string[];
  'data-sources-persona'?: boolean;
  'recurring-enabled'?: boolean;
  'always-recur'?: boolean;
  'interval-seconds'?: number;
  lists?: string[];
  preset?: WatchlistPresetType;
  'match-hypocorisms'?: boolean;
  'match-abbreviations'?: boolean;
  'match-transliterations'?: boolean;
  'match-joined'?: boolean;
  'match-first-last'?: boolean;
  'birthdate-match-type'?: BirthdateMatchType;
  'minimum-characters-for-fuzziness'?: number;
  'exact-match-country'?: boolean;
  'match-birthplace'?: boolean;
  'match-citizenship'?: boolean;
  'match-nationality'?: boolean;
  'match-residency'?: boolean;
}

export type ReportTemplateVersionAdverseMedia = ApiResource<
  ReportTemplateVersionAttributesAdverseMedia,
  Record<string, never>,
  ApiModel.ReportTemplateVersionAdverseMedia
>;

export type ReportTemplateVersionBusinessAdverseMedia = ApiResource<
  ReportTemplateVersionAttributesBusinessAdverseMedia,
  Record<string, never>,
  ApiModel.ReportTemplateVersionBusinessAdverseMedia
>;

export type ReportTemplateVersionBusinessAssociatedPersons = ApiResource<
  ReportTemplateVersionAttributesBusinessAssociatedPersons,
  Record<string, never>,
  ApiModel.ReportTemplateVersionBusinessAssociatedPersons
>;

export type ReportTemplateVersionBusinessClassification = ApiResource<
  ReportTemplateVersionAttributesBusinessClassification,
  Record<string, never>,
  ApiModel.ReportTemplateVersionBusinessClassification
>;

export type ReportTemplateVersionBusinessWatchlist = ApiResource<
  ReportTemplateVersionAttributesBusinessWatchlist,
  Record<string, never>,
  ApiModel.ReportTemplateVersionBusinessWatchlist
>;

export type ReportTemplateVersionCryptoAddressWatchlist = ApiResource<
  ReportTemplateVersionAttributesCryptoAddressWatchlist,
  Record<string, never>,
  ApiModel.ReportTemplateVersionCryptoAddressWatchlist
>;

export type ReportTemplateVersionCustomList = ApiResource<
  ReportTemplateVersionAttributesCustomList,
  Record<string, never>,
  ApiModel.ReportTemplateVersionCustomList
>;

export type ReportTemplateVersionPoliticallyExposedPerson = ApiResource<
  ReportTemplateVersionAttributesPoliticallyExposedPerson,
  Record<string, never>,
  ApiModel.ReportTemplateVersionPoliticallyExposedPerson
>;

export type ReportTemplateVersionWatchlist = ApiResource<
  ReportTemplateVersionAttributesWatchlist,
  Record<string, never>,
  ApiModel.ReportTemplateVersionWatchlist
>;

export type ReportTemplateVersion =
  | ReportTemplateVersionAdverseMedia
  | ReportTemplateVersionBusinessAdverseMedia
  | ReportTemplateVersionBusinessAssociatedPersons
  | ReportTemplateVersionBusinessClassification
  | ReportTemplateVersionBusinessWatchlist
  | ReportTemplateVersionCryptoAddressWatchlist
  | ReportTemplateVersionCustomList
  | ReportTemplateVersionPoliticallyExposedPerson
  | ReportTemplateVersionWatchlist;
